// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import './styles.css';
import { loadCore, loadMeta, getMeta } from './data';
import { currentRoute, navigate, onRouteChange, DEFAULT_VIEW } from './router';
import { initTooltip } from './components/tooltip';
import { initGlossary } from './components/glossary';
import { closeAllOverlays } from './overlay';
import { openAbout } from './about';
import { openRegion } from './components/regionDrawer';
import { esc, num, fy } from './format';

import { renderLedger } from './views/ledger';
import { renderEngines } from './views/engines';
import { renderGround } from './views/ground';
import { renderField } from './views/field';
import { renderTrails } from './views/trails';
import { renderMechanism } from './views/mechanism';
import { renderCostGap } from './views/costgap';
import { renderSpread } from './views/spread';

/** Nav labels are WORDS ONLY — never a count badge. Counts belong in the views. */
const VIEWS: { id: string; label: string; render: (host: HTMLElement) => void | Promise<void>; narrow?: boolean }[] = [
  { id: 'ledger', label: 'The Ledger', render: renderLedger },
  { id: 'engines', label: 'Two Engines', render: renderEngines },
  { id: 'ground', label: 'Where Farming Pays', render: renderGround },
  { id: 'field', label: 'The Field', render: renderField },
  { id: 'trails', label: 'Yield Trails', render: renderTrails },
  { id: 'mechanism', label: 'The Mechanism', render: renderMechanism },
  { id: 'costgap', label: 'Cost Gap', render: renderCostGap },
  { id: 'spread', label: 'The Spread', render: renderSpread },
];

function shell(): { main: HTMLElement } {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <a class="skip-link" href="#main-content">Skip to content</a>
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#/${DEFAULT_VIEW}">
          <svg class="brand-mark" viewBox="0 0 64 64" aria-hidden="true">
            <rect width="64" height="64" rx="12" fill="#2f5d3f"/>
            <path d="M4 44c12-6 26-8 38-5 8 2 14 5 18 8v9H4z" fill="#7a5c33"/>
            <path d="M4 39c12-6 26-8 38-5 8 2 14 5 18 8v4c-4-3-10-6-18-8-12-3-26-1-38 5z" fill="#a9803f"/>
            <path d="M9 37 L22 30 L33 33 L45 19 L56 12" fill="none" stroke="#e8c86a" stroke-width="4.2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="56" cy="12" r="4.6" fill="#e8c86a"/>
          </svg>
          <span class="brand-name">Farm Economics</span>
        </a>
        <div class="header-spacer"></div>
        <button type="button" class="icon-btn" id="about-btn" aria-label="About this site, its sources and its limitations">About</button>
      </div>
    </header>
    <nav class="site-nav" aria-label="Views">
      <div class="nav-inner" id="nav-inner"></div>
    </nav>
    <main class="main-content" id="main-content" tabindex="-1"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-sources" id="footer-sources"></div>
        <div>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></div>
      </div>
    </footer>
  `;

  const nav = app.querySelector<HTMLElement>('#nav-inner')!;
  for (const view of VIEWS) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'nav-tab';
    b.textContent = view.label;
    b.dataset.view = view.id;
    b.addEventListener('click', () => navigate(view.id));
    nav.append(b);
  }

  app.querySelector<HTMLButtonElement>('#about-btn')!
    .addEventListener('click', () => openAbout(getMeta()));

  // The skip link must not reach the router. `href="#main-content"` sets
  // location.hash, which fires hashchange, which the router would read as the
  // view "main-content" — an unknown view, so it would fall back to the default
  // and throw the reader out of whatever they were reading.
  app.querySelector<HTMLAnchorElement>('.skip-link')!.addEventListener('click', (e) => {
    e.preventDefault();
    document.getElementById('main-content')?.focus();
  });

  return { main: app.querySelector<HTMLElement>('#main-content')! };
}

function markActive(id: string): void {
  for (const tab of document.querySelectorAll<HTMLElement>('.nav-tab')) {
    const on = tab.dataset.view === id;
    if (on) tab.setAttribute('aria-current', 'page');
    else tab.removeAttribute('aria-current');
    if (on) tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

let renderToken = 0;

async function route(main: HTMLElement): Promise<void> {
  // An overlay opened on one view must not survive into the next: on a phone a
  // drawer is full-width, so the reader would appear to be browsing behind it.
  closeAllOverlays();

  const { view, params } = currentRoute();
  const entry = VIEWS.find((v) => v.id === view) ?? VIEWS[0];
  markActive(entry.id);

  // A view render is async. Without a token, navigating twice quickly lets a
  // slow first render append its DOM into a host the second render already
  // replaced, and the reader ends up with two views stacked.
  const token = ++renderToken;
  main.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = `view-wrap${entry.narrow ? ' narrow' : ''}`;
  main.append(wrap);
  try {
    await entry.render(wrap);
  } catch (err) {
    if (token !== renderToken) return;
    wrap.innerHTML = `<div class="error-state"><strong>This view failed to render.</strong>
      <p class="small">${esc(err instanceof Error ? err.message : String(err))}</p></div>`;
    return;
  }
  if (token !== renderToken) return;

  // Deep link into a region dossier, so a shared URL reopens what was shared.
  const region = params.get('region');
  if (region) openRegion(region);
}

async function boot(): Promise<void> {
  const { main } = shell();
  main.innerHTML = '<div class="view-wrap"><div class="loading">Loading 36 years of the ABARES farm survey…</div></div>';

  let meta;
  try {
    [meta] = await Promise.all([loadMeta(), loadCore()]);
    document.querySelector('#footer-sources')!.innerHTML = `
      Source: <a href="https://www.agriculture.gov.au/abares/data/farm-data-portal" target="_blank" rel="noopener">ABARES Farm Data Portal</a>
      (CC BY 4.0) — the Australian Agricultural and Grazing Industries Survey, ${num(meta.variableCount)} variables across
      ${num(meta.regionCount)} regions, ${fy(meta.firstYear)} to ${fy(meta.lastYear)}. Boundaries: ABARES AAGIS regions.
      Data as at ${esc(meta.asAt)}. Survey estimates, not a census. Independent project; not affiliated with ABARES.`;
  } catch (err) {
    main.innerHTML = `<div class="view-wrap"><div class="error-state">
      <strong>The farm survey data could not be loaded.</strong>
      <p class="small">${esc(err instanceof Error ? err.message : String(err))}</p>
      <p class="small">The original data is published at
        <a href="https://www.agriculture.gov.au/abares/data/farm-data-portal">the ABARES Farm Data Portal</a>.</p>
      <button type="button" class="btn" id="retry">Try again</button></div></div>`;
    document.getElementById('retry')?.addEventListener('click', () => location.reload());
    return;
  }

  initTooltip();
  initGlossary();
  if (!location.hash) history.replaceState(null, '', `#/${DEFAULT_VIEW}`);
  await route(main);
  onRouteChange(() => { void route(main); });
}

void boot();
